import React, { useState, useEffect } from 'react'
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import NoteIcon from "@mui/icons-material/Note";
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ShareIcon from '@mui/icons-material/Share';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import LanguageIcon from '@mui/icons-material/Language';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import GoogleIcon from '@mui/icons-material/Google';
import Tooltip from '@mui/material/Tooltip';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import CloseIcon from '@mui/icons-material/Close';

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Link from "@mui/material/Link";
import { useNavigate, useParams } from 'react-router-dom';
import { getListById, getUserById, removeProfileFromList, shareList } from '../../services/userService';
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import ProfileView from '../profileDetails/ProfileView';

// Helper function to capitalize first letter of a string
const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Helper function to get social media icon
const getSocialIcon = (network) => {
  const networkLower = network.toLowerCase();
  switch (networkLower) {
    case 'linkedin':
      return <LinkedInIcon sx={{ fontSize: '25px', color: '#0077b5' }} />;
    case 'twitter':
      return <TwitterIcon sx={{ fontSize: '25px', color: '#1DA1F2' }} />;
    case 'facebook':
      return <FacebookIcon sx={{ fontSize: '25px', color: '#4267B2' }} />;
    case 'instagram':
      return <InstagramIcon sx={{ fontSize: '25px', color: '#E4405F' }} />;
    case 'github':
      return <GitHubIcon sx={{ fontSize: '25px', color: '#333' }} />;
    case 'gravatar':
      return <AccountCircleIcon sx={{ fontSize: '25px', color: '#1e1e1e' }} />;
    case 'angellist':
    case 'angel':
      return <BusinessIcon sx={{ fontSize: '25px', color: '#000000' }} />;
    case 'klout':
      return <StarIcon sx={{ fontSize: '25px', color: '#ff6b35' }} />;
    case 'foursquare':
      return <LocationOnIcon sx={{ fontSize: '25px', color: '#f94877' }} />;
    case 'aboutme':
    case 'about.me':
      return <PersonIcon sx={{ fontSize: '25px', color: '#00405d' }} />;
    case 'google':
    case 'google+':
      return <GoogleIcon sx={{ fontSize: '25px', color: '#4285f4' }} />;
    default:
      return <LanguageIcon sx={{ fontSize: '25px', color: '#666' }} />;
  }
};

// Helper function to get social media URL
const getSocialUrl = (socialProfile) => {
  if (socialProfile.url) {
    // If URL doesn't start with http, add https://
    return socialProfile.url.startsWith('http') 
      ? socialProfile.url 
      : `https://${socialProfile.url}`;
  }
  // Fallback to constructing URL from network and username
  const networkLower = socialProfile.network.toLowerCase();
  const username = socialProfile.username || '';
  switch (networkLower) {
    case 'linkedin':
      return `https://linkedin.com/in/${username}`;
    case 'twitter':
      return `https://twitter.com/${username}`;
    case 'facebook':
      return `https://facebook.com/${username}`;
    case 'instagram':
      return `https://instagram.com/${username}`;
    case 'github':
      return `https://github.com/${username}`;
    case 'gravatar':
      return `https://gravatar.com/${username}`;
    case 'angellist':
    case 'angel':
      return `https://angel.co/${username}`;
    case 'klout':
      return `https://klout.com/${username}`;
    case 'foursquare':
      return `https://foursquare.com/${username}`;
    case 'aboutme':
    case 'about.me':
      return `https://about.me/${username}`;
    case 'google':
    case 'google+':
      return `https://plus.google.com/${username}`;
    default:
      return username ? `https://${username}` : '#';
  }
};

// Helper function to merge profiles with feedback data
const mergeProfilesWithFeedback = (profiles = [], feedbackList = []) => {
  if (!Array.isArray(profiles) || profiles.length === 0) return [];
  if (!Array.isArray(feedbackList) || feedbackList.length === 0) {
    return profiles.map((profile) => ({
      ...profile,
      feedbackStatus: profile.feedbackStatus || profile.decisionStatus || 'pending',
      feedbackNote: profile.feedbackNote || profile.decisionNote || '',
      feedbackUpdatedAt: profile.feedbackUpdatedAt || profile.decisionUpdatedAt || null
    }));
  }

  return profiles.map((profile) => {
    const feedback = feedbackList.find(
      (item) => item.linkedinUsername === profile.linkedinUsername
    );

    if (!feedback) {
      return {
        ...profile,
        feedbackStatus: profile.feedbackStatus || profile.decisionStatus || 'pending',
        feedbackNote: profile.feedbackNote || profile.decisionNote || '',
        feedbackUpdatedAt: profile.feedbackUpdatedAt || profile.decisionUpdatedAt || null
      };
    }

    return {
      ...profile,
      feedbackStatus: feedback.status || 'pending',
      feedbackNote: feedback.note || '',
      feedbackUpdatedAt: feedback.updatedAt || profile.decisionUpdatedAt || null
    };
  });
};

const UserListsDetails = () => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [emailChips, setEmailChips] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [nuggetsDialogOpen, setNuggetsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchListDetails = async () => {
      try {
        setLoading(true);
        const response = await getListById(listId);
        console.log('List response:', response);
        if (response.status === 'success') {
          setList(response.data);
          
          // Merge profiles with feedback data
          const mergedProfiles = mergeProfilesWithFeedback(
            response.data.profiles || [],
            response.data.candidateFeedback || []
          );
          setProfiles(mergedProfiles);
        }
      } catch (error) {
        console.error('Error fetching list details:', error);
        setError('Failed to load list details');
      } finally {
        setLoading(false);
      }
    };

    if (listId) {
      fetchListDetails();
    }
  }, [listId]);

  const handleDeleteProfile = async (linkedinUsername) => {
    try {
      // Optimistically update the UI
      const updatedProfiles = profiles.filter(profile => profile.linkedinUsername !== linkedinUsername);
      setProfiles(updatedProfiles);
      
      // Call API to remove profile from list in backend
      const response = await removeProfileFromList(listId, linkedinUsername);
      if (response.status === 'success') {
      } else {
        // If API call fails, revert the UI change
        setProfiles(profiles);
        console.error('Failed to remove profile from list');
      }
    } catch (error) {
      // If API call fails, revert the UI change
      setProfiles(profiles);
      console.error('Error removing profile:', error);
    }
  };

  // Profile click handler
  const handleProfileClick = (profile) => {
    setSelectedProfile(profile);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleShare = () => {
    setShareDialogOpen(true);
  };

  const handleProfileShare = async (profile) => {
  try {
    const res = await fetch("/api/profile-share/share-profile", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        profileData: profile
      })
    });
    const data = await res.json();
    const shareUrl = `${window.location.origin}/public/profile/${data.token}`;
    await navigator.clipboard.writeText(shareUrl);
    alert("Link copied! (valid for 7 days)");
  } catch (err) {
    console.error(err);
    alert("Failed to generate link");
  }
};

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setEmailChips([]);
    setEmailInput('');
  };

  const handleEmailInputChange = (event, newInputValue) => {
    setEmailInput(newInputValue);
  };

  const handleEmailChipsChange = (event, newValue) => {
    setEmailChips(newValue);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendShare = async () => {
    // Validate all email chips
    const invalidEmails = emailChips.filter(email => !validateEmail(email));
    
    if (invalidEmails.length > 0) {
      alert('Please enter valid email addresses');
      return;
    }
    
    if (emailChips.length === 0) {
      alert('Please enter at least one email address');
      return;
    }
    
    try {
      setShareLoading(true);
      
      // Call the API to share the list
      const response = await shareList(listId, emailChips);
      
      if (response.status === 'success') {
        alert(`List shared successfully with ${emailChips.length} email(s)!`);
        handleShareDialogClose();
        
        // Refresh list data to show feedback column if it wasn't shared before
        const refreshResponse = await getListById(listId);
        if (refreshResponse.status === 'success') {
          setList(refreshResponse.data);
          const mergedProfiles = mergeProfilesWithFeedback(
            refreshResponse.data.profiles || [],
            refreshResponse.data.candidateFeedback || []
          );
          setProfiles(mergedProfiles);
        }
      } else {
        alert('Failed to share list. Please try again.');
      }
    } catch (error) {
      console.error('Error sharing list:', error);
      alert('Failed to share list. Please try again.');
    } finally {
      setShareLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !shareLoading) {
      event.preventDefault();
      // If there's text in the input, add it as a chip
      if (emailInput.trim()) {
        const email = emailInput.trim();
        if (validateEmail(email) && !emailChips.includes(email)) {
          setEmailChips([...emailChips, email]);
          setEmailInput('');
        } else if (!validateEmail(email)) {
          alert('Please enter a valid email address');
        } else {
          alert('This email is already added');
        }
      } else {
        // If no input, try to send share
        handleSendShare();
      }
    }
  };

  const handleFeedbackClick = (profile) => {
    setSelectedFeedback({
      fullName: profile.fullName || 'N/A',
      status: profile.feedbackStatus || 'pending',
      note: profile.feedbackNote || '',
      updatedAt: profile.feedbackUpdatedAt
    });
    setFeedbackDialogOpen(true);
  };

  const handleFeedbackDialogClose = () => {
    setFeedbackDialogOpen(false);
    setSelectedFeedback(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!list) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="warning">List not found</Alert>
      </Box>
    );
  }

  // Check if list has been shared (has candidateFeedback or shared properties)
  const isListShared = list.candidateFeedback && list.candidateFeedback.length > 0 ||
                       list.sharedWith && list.sharedWith.length > 0 ||
                       list.sharedUsers && list.sharedUsers.length > 0 ||
                       list.isShared === true;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column',}}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: '#666' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
          {list.name}
        </Typography>
        <IconButton onClick={handleShare} sx={{ color: '#666', ml: 1 }}>
          <ShareIcon />
        </IconButton>
      </Box>
      
      {list.description && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ color: '#666', display: 'inline' }}>
            {(() => {
              const words = list.description.split(' ');
              if (words.length > 20) {
                return words.slice(0, 20).join(' ') + '... ';
              }
              return list.description;
            })()}
          </Typography>
          {list.description.split(' ').length > 20 && (
            <Link
              component="button"
              variant="body1"
              onClick={() => setDescriptionDialogOpen(true)}
              sx={{
                color: '#1976d2',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': { color: '#1565c0' },
                ml: 0.5
              }}
            >
              Show More
            </Link>
          )}
        </Box>
      )}

      {list.nuggets && String(list.nuggets).trim() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
            Nuggets: 
            <Typography variant="body1" sx={{ color: '#666', display: 'inline' }}>
            {(() => {
              const words = String(list.nuggets).split(' ');
              if (words.length > 20) {
                return words.slice(0, 20).join(' ') + '... ';
              }
              return String(list.nuggets);
            })()}
          </Typography>
          {String(list.nuggets).split(' ').length > 20 && (
            <Link
              component="button"
              variant="body1"
              onClick={() => setNuggetsDialogOpen(true)}
              sx={{
                color: '#1976d2',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': { color: '#1565c0' },
                ml: 0.5
              }}
            >
              Show More
            </Link>
          )}
          </Typography>
          
        </Box>
      )}
      
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
        Profiles ({profiles.length})
      </Typography>
      
      {profiles.length === 0 ? (
        <Alert severity="info">No profiles found in this list</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Picture & Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Social Footprints</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current Location</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '300px' }}>Summary</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Fitment Score</TableCell>
                {isListShared && (
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Feedback</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles.map((profile, index) => {
                const fitmentPoints = Array.isArray(profile.fitmentSummary)
                  ? profile.fitmentSummary
                  : profile?.fitmentSummary
                    ? [String(profile.fitmentSummary)]
                    : [];

                return (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <img src={profile.profilePicture} alt="Profile" style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                        <Typography 
                          sx={{ 
                            cursor: 'pointer', 
                            color: '#1976d2', 
                            textDecoration: 'underline',
                            fontSize: '0.875rem',
                            textAlign: 'center',
                            '&:hover': { color: '#1565c0' }
                          }}
                          onClick={() => handleProfileClick(profile)}
                        >
                          {profile.fullName || 'N/A'}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : profile.socialProfiles && profile.socialProfiles.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                        {profile.socialProfiles.map((socialProfile, socialIndex) => (
                          <Tooltip 
                            key={socialIndex} 
                            title={`${socialProfile.network}: ${socialProfile.url || socialProfile.username || ''}`}
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                const url = getSocialUrl(socialProfile);
                                if (url && url !== '#') {
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              sx={{ 
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                                padding: '1px'
                              }}
                            >
                              {getSocialIcon(socialProfile.network)}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    ) : profile.linkedinLink ? (
                      <IconButton 
                        size={'large'} 
                        onClick={() => {
                          const url = profile.linkedinLink.startsWith('http') 
                            ? profile.linkedinLink 
                            : `https://${profile.linkedinLink}`;
                          window.open(url, '_blank');
                        }}
                        sx={{ '&:hover': { backgroundColor: 'rgba(0, 119, 181, 0.1)' } }}
                      >
                        <LinkedInIcon sx={{ fontSize: '25px', color: '#0077b5' }} />
                      </IconButton>
                    ) : (
                      <Typography color="text.secondary">No link</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {profile.company ? capitalizeFirstLetter(profile.company) : 'N/A'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {profile.position && (
                          <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            {capitalizeFirstLetter(profile.position)}
                          </Typography>
                        )}
                        {profile.industry && (
                          <Typography variant="body2" sx={{ color: '#999', fontSize: '0.813rem' }}>
                            Industry: {capitalizeFirstLetter(profile.industry)}
                          </Typography>
                        )}
                        {!profile.position && !profile.industry && (
                          <Typography variant="body2">N/A</Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      profile.location ? capitalizeFirstLetter(profile.location) : 'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : fitmentPoints.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {fitmentPoints.map((point, idx) => (
                          <Typography 
                            key={idx} 
                            variant="body2" 
                            sx={{ 
                              color: '#666',
                              fontSize: '0.875rem',
                              lineHeight: 1.5,
                              '&::before': {
                                content: '"• "',
                                color: '#1BE885',
                                fontWeight: 'bold'
                              }
                            }}
                          >
                            {point}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No summary available</Typography>
                    )}
                  </TableCell>
               <TableCell sx={{ textAlign: 'center' }}>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : profile.fitmentScore !== undefined && profile.fitmentScore !== null ? (
                      <>
                        <Chip 
                          label={`${profile.fitmentScore}%`}
                          sx={{ 
                            bgcolor: profile.fitmentScore >= 85 ? '#4caf50' : 
                                     profile.fitmentScore >= 61 ? '#ffeb3b' : 
                                     profile.fitmentScore >= 41 ? '#ff9800' : 
                                     '#f44336',
                            color: profile.fitmentScore >= 61 ? '#000' : '#fff',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            minWidth: '60px'
                          }}
                        /> 
                        
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </TableCell>
                  {isListShared && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      {profile.error ? (
                        <Typography color="error">Not found</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Chip
                            label={profile.feedbackStatus === 'accepted' ? 'Accepted' : 
                                   profile.feedbackStatus === 'rejected' ? 'Rejected' : 'Pending'}
                            sx={{
                              bgcolor: profile.feedbackStatus === 'accepted' ? '#4caf50' :
                                       profile.feedbackStatus === 'rejected' ? '#f44336' :
                                       '#ff9800',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              minWidth: '80px'
                            }}
                          />
                          {profile.feedbackNote && profile.feedbackNote.trim() && (
                            <Tooltip title="Click to view feedback" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleFeedbackClick(profile)}
                                sx={{
                                  color: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                  }
                                }}
                              >
                                <NoteIcon sx={{ fontSize: '20px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  )}
                <TableCell sx={{ textAlign: 'center' }}>
                  {/* <IconButton 
                    onClick={() => handleProfileShare(profile)}
                    sx={{ color: '#1976d2' }}
                  >
                    <ShareIcon />
                  </IconButton> */}

                  <IconButton 
                    onClick={() => handleDeleteProfile(profile.linkedinUsername)}
                    sx={{ 
                      color: '#666',
                      '&:hover': { 
                        color: '#f44336',
                        backgroundColor: 'rgba(244, 67, 54, 0.1)' 
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Profile Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth={false} PaperProps={{sx: { width: '60vw', height: '80vh', marginTop: '64px', marginLeft: '240px', marginRight: '240px', borderRadius: 2, }}}>
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ width: '100%', height: '100%', p: 2 }}>
          {selectedProfile && (
            <ProfileView profileData={selectedProfile} />
          )}
        </Box>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleShareDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Share List
          <IconButton
            aria-label="close"
            onClick={handleShareDialogClose}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Share "{list.name}" with others by entering their email addresses below.
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={emailChips}
            onChange={handleEmailChipsChange}
            inputValue={emailInput}
            onInputChange={handleEmailInputChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                  onDelete={() => {
                    const newChips = emailChips.filter((_, i) => i !== index);
                    setEmailChips(newChips);
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                margin="dense"
                label="Email Addresses"
                placeholder="Enter email address and press Enter"
                variant="outlined"
                onKeyPress={handleKeyPress}
                helperText="Enter email addresses and press Enter to add. Press Enter again to send."
                sx={{ mt: 1 }}
              />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleShareDialogClose} color="secondary" disabled={shareLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendShare} 
            variant="contained" 
            color="primary"
            disabled={shareLoading || emailChips.length === 0}
          >
            {shareLoading ? 'Sending...' : 'Send Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialogOpen} onClose={handleFeedbackDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Feedback Details
          <IconButton
            aria-label="close"
            onClick={handleFeedbackDialogClose}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                  Candidate
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedFeedback.fullName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                  Status
                </Typography>
                <Chip
                  label={selectedFeedback.status === 'accepted' ? 'Accepted' :
                         selectedFeedback.status === 'rejected' ? 'Rejected' : 'Pending'}
                  sx={{
                    bgcolor: selectedFeedback.status === 'accepted' ? '#4caf50' :
                             selectedFeedback.status === 'rejected' ? '#f44336' :
                             '#ff9800',
                    color: '#fff',
                    fontWeight: 600
                  }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                  Feedback Note
                </Typography>
                {selectedFeedback.note && selectedFeedback.note.trim() ? (
                  <Typography variant="body1" sx={{ 
                    color: '#333',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1
                  }}>
                    {selectedFeedback.note}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                    No feedback note available
                  </Typography>
                )}
              </Box>
              {selectedFeedback.updatedAt && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                    Last Updated
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {new Date(selectedFeedback.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleFeedbackDialogClose} variant="contained" sx={{
            bgcolor: '#1BE885',
            '&:hover': { bgcolor: '#17d077' }
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Description Dialog */}
      <Dialog 
        open={descriptionDialogOpen} 
        onClose={() => setDescriptionDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          List Description
          <IconButton
            aria-label="close"
            onClick={() => setDescriptionDialogOpen(false)}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#666', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {list?.description || 'No description available'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nuggets Dialog */}
      <Dialog
        open={nuggetsDialogOpen}
        onClose={() => setNuggetsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nuggets
          <IconButton
            aria-label="close"
            onClick={() => setNuggetsDialogOpen(false)}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#666', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {list?.nuggets || '—'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuggetsDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UserListsDetails
